import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useData } from '../context/DataContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  Upload, 
  FileSpreadsheet, 
  ArrowLeft,
  Loader2,
  CheckCircle,
  Download,
  Trash2,
  UserMinus,
  UserPlus,
  RefreshCw,
  X,
  Mail,
  User,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Building,
  Check,
  Clock,
  MoreHorizontal,
  FolderOpen,
  Calendar,
  Tag,
  Database,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MitarbeiterAbgleich = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { dataStatus, isDataLoaded } = useData();
  const [employeeFile, setEmployeeFile] = useState(null);
  const [isComparing, setIsComparing] = useState(false);
  const [result, setResult] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dragOverEmployee, setDragOverEmployee] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmedItems, setConfirmedItems] = useState({}); // Track confirmed items locally
  const [isConfirming, setIsConfirming] = useState(null); // Track which item is being confirmed

  const colors = {
    dark: '#0D2440',
    blue: '#2E5E99',
    gray: '#5A7A9B',
    border: '#C8D8E8',
    bg: '#E7F0FA',
  };

  // Only load result if explicitly requested via URL parameter
  // Do NOT auto-load recent results - user should always start fresh
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const loadResult = async () => {
      const resultId = searchParams.get('result');
      
      // Only load if there's a specific result ID in the URL
      if (!resultId) {
        setIsLoading(false);
        hasLoadedRef.current = true;
        return;
      }
      
      setIsLoading(true);
      
      try {
        const response = await axios.get(`${API}/services/mitarbeiterabgleich/results/${resultId}`);
        const loadedResult = response.data;
        
        // Only show result if it has actual data
        const hasData = loadedResult.summary && (
          loadedResult.summary.exact_matches > 0 ||
          loadedResult.summary.likely_matches > 0 ||
          loadedResult.summary.needs_review > 0 ||
          loadedResult.summary.missing_in_eMatrixsys > 0 ||
          loadedResult.summary.former_employees > 0
        );
        
        if (hasData) {
          setResult(loadedResult);
        } else {
          // Clear empty result from URL
          window.history.replaceState(null, '', window.location.pathname);
        }
      } catch (error) {
        console.error('Error loading result:', error);
        // If result not found, clear the URL param
        window.history.replaceState(null, '', window.location.pathname);
      } finally {
        setIsLoading(false);
        hasLoadedRef.current = true;
      }
    };

    loadResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileDrop = useCallback((e, setFile, setDragOver) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer?.files[0] || e.target?.files[0];
    if (file) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (validTypes.includes(fileExtension)) {
        setFile(file);
        toast.success(`Datei "${file.name}" ausgewählt`);
      } else {
        toast.error('Bitte nur CSV oder Excel-Dateien hochladen');
      }
    }
  }, []);

  const handleDragOver = (e, setDragOver) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e, setDragOver) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleCompare = async () => {
    if (!employeeFile) {
      toast.error('Bitte Mitarbeiterliste auswählen');
      return;
    }

    if (!isDataLoaded) {
      toast.error('Kein Datenstand geladen. Bitte laden Sie zuerst einen Datenstand.');
      return;
    }

    setIsComparing(true);
    
    try {
      const formData = new FormData();
      formData.append('employee_file', employeeFile);

      const response = await axios.post(
        `${API}/services/mitarbeiterabgleich/compare-with-data`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setResult(response.data);
      setConfirmedItems({});
      // Update URL with result ID so user can return to this result
      window.history.replaceState(null, '', `?result=${response.data.id}`);
      toast.success('Vergleich erfolgreich abgeschlossen');
    } catch (error) {
      console.error('Comparison error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Vergleich');
    } finally {
      setIsComparing(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.id) return;
    
    try {
      const response = await axios.get(
        `${API}/services/mitarbeiterabgleich/download/${result.id}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mitarbeiterabgleich_${result.id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report heruntergeladen');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Fehler beim Herunterladen');
    }
  };

  const handleDelete = async () => {
    if (!result?.id) return;
    
    setIsDeleting(true);
    
    try {
      await axios.delete(`${API}/services/mitarbeiterabgleich/results/${result.id}`);
      toast.success('Daten erfolgreich gelöscht');
      setResult(null);
      setEmployeeFile(null);
      setConfirmedItems({});
      setIsDeleteOpen(false);
      window.history.replaceState(null, '', window.location.pathname);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Fehler beim Löschen');
    } finally {
      setIsDeleting(false);
    }
  };

  const resetAll = () => {
    setResult(null);
    setEmployeeFile(null);
    setConfirmedItems({});
    window.history.replaceState(null, '', window.location.pathname);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Confirm a match (saves to database for future comparisons)
  // Also moves the item to the appropriate category and updates statistics
  const handleConfirmMatch = async (person, status, currentCategory) => {
    const key = `${person.employee?.email || person.email || ''}_${person.employee?.name || person.name || ''}`;
    setIsConfirming(key);
    
    try {
      await axios.post(`${API}/services/mitarbeiterabgleich/confirm`, {
        employee_name: person.employee?.name || person.name || '',
        employee_email: person.employee?.email || person.email || '',
        eMatrixsys_name: person.eMatrixsys?.name || '',
        eMatrixsys_email: person.eMatrixsys?.email || '',
        status: status,
      });
      
      // Update local state to show confirmation
      setConfirmedItems(prev => ({
        ...prev,
        [key]: status
      }));
      
      // Move item to the appropriate category and update statistics
      setResult(prevResult => {
        if (!prevResult) return prevResult;
        
        const newResult = { ...prevResult };
        
        // Create a copy of the person with confirmed status for display
        const confirmedPerson = { 
          ...person, 
          confirmed_status: status,
          original_category: currentCategory 
        };
        
        // Map status to target category
        const statusToCategory = {
          'confirmed_match': 'exact_matches',
          'confirmed_former': 'former_employees', 
          'confirmed_new': 'missing_in_eMatrixsys',
          'needs_review': 'needs_review',
          'ignored': null // Remove from all
        };
        
        // Map category names to array keys
        const categoryKeys = {
          'exact': 'exact_matches',
          'likely': 'likely_matches',
          'review': 'needs_review',
          'missing': 'missing_in_eMatrixsys',
          'former': 'former_employees'
        };
        
        const sourceKey = categoryKeys[currentCategory];
        const targetKey = statusToCategory[status];
        
        // Remove from source category
        if (sourceKey && newResult[sourceKey]) {
          newResult[sourceKey] = newResult[sourceKey].filter(item => {
            const itemKey = `${item.employee?.email || item.email || ''}_${item.employee?.name || item.name || ''}`;
            return itemKey !== key;
          });
        }
        
        // Add to target category (if not ignored)
        if (targetKey && targetKey !== sourceKey) {
          if (!newResult[targetKey]) newResult[targetKey] = [];
          
          // Format the person for the target category
          let formattedPerson;
          if (targetKey === 'missing_in_eMatrixsys' || targetKey === 'former_employees') {
            // These categories have a simpler structure
            formattedPerson = {
              name: person.employee?.name || person.name || '',
              email: person.employee?.email || person.email || '',
              department: person.employee?.department || person.department || '',
              data: person.employee?.data || person.data || {},
              confirmed_status: status
            };
          } else {
            formattedPerson = { ...confirmedPerson };
          }
          
          newResult[targetKey] = [...newResult[targetKey], formattedPerson];
        }
        
        // Update summary counts
        const summaryKeys = {
          'exact_matches': 'exact_matches',
          'likely_matches': 'likely_matches',
          'needs_review': 'needs_review',
          'missing_in_eMatrixsys': 'missing_in_eMatrixsys',
          'former_employees': 'former_employees'
        };
        
        newResult.summary = { ...newResult.summary };
        
        // Recalculate counts based on actual array lengths
        Object.keys(summaryKeys).forEach(arrayKey => {
          const summaryKey = summaryKeys[arrayKey];
          newResult.summary[summaryKey] = (newResult[arrayKey] || []).length;
        });
        
        return newResult;
      });
      
      // Status-specific toast messages
      const statusMessages = {
        'confirmed_match': 'Übereinstimmung bestätigt',
        'confirmed_former': 'Als ehemalig markiert',
        'confirmed_new': 'Als neu anzulegen markiert',
        'needs_review': 'Zur Prüfung markiert',
        'ignored': 'Eintrag ignoriert'
      };
      
      toast.success(`${statusMessages[status] || 'Gespeichert'} - wird bei zukünftigen Abgleichen berücksichtigt`);
    } catch (error) {
      console.error('Confirm error:', error);
      toast.error('Fehler beim Speichern der Bestätigung');
    } finally {
      setIsConfirming(null);
    }
  };

  const FileUploadZone = ({ file, setFile, title, description, dragOver, setDragOver, testId }) => (
    <div
      className={`upload-zone p-6 text-center ${dragOver ? 'drag-over' : ''}`}
      onDrop={(e) => handleFileDrop(e, setFile, setDragOver)}
      onDragOver={(e) => handleDragOver(e, setDragOver)}
      onDragLeave={(e) => handleDragLeave(e, setDragOver)}
      data-testid={testId}
    >
      {file ? (
        <div className="flex items-center justify-center gap-3">
          <FileSpreadsheet className="h-8 w-8" style={{ color: colors.blue }} />
          <div className="text-left">
            <p className="font-medium" style={{ color: colors.dark }}>{file.name}</p>
            <p className="text-sm" style={{ color: colors.gray }}>
              {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setFile(null)}
            className="ml-2"
            data-testid={`${testId}-remove`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <>
          <Upload className="h-10 w-10 mx-auto mb-4" style={{ color: colors.gray }} />
          <h4 className="font-medium mb-1" style={{ color: colors.dark }}>{title}</h4>
          <p className="text-sm mb-4" style={{ color: colors.gray }}>{description}</p>
          <label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => handleFileDrop(e, setFile, setDragOver)}
              className="hidden"
              data-testid={`${testId}-input`}
            />
            <Button variant="outline" asChild>
              <span>Datei auswählen</span>
            </Button>
          </label>
        </>
      )}
    </div>
  );

  const PersonCard = ({ person, type, matchType, reason, onConfirm, isConfirming, confirmedStatus, category }) => {
    const [isOpen, setIsOpen] = useState(false);
    const personKey = `${person.employee?.email || person.email || ''}_${person.employee?.name || person.name || ''}`;
    const isThisConfirming = isConfirming === personKey;
    
    const getIcon = () => {
      if (confirmedStatus === 'confirmed_match') return <Check className="h-4 w-4 text-green-600" />;
      if (confirmedStatus === 'confirmed_former') return <UserMinus className="h-4 w-4 text-gray-500" />;
      
      switch (type) {
        case 'exact': return <Mail className="h-4 w-4 text-green-600" />;
        case 'likely': return <User className="h-4 w-4 text-blue-600" />;
        case 'review': return <HelpCircle className="h-4 w-4 text-amber-600" />;
        case 'missing': return <UserPlus className="h-4 w-4 text-orange-600" />;
        case 'former': return <UserMinus className="h-4 w-4 text-red-600" />;
        default: return <User className="h-4 w-4" />;
      }
    };
    
    const getBgColor = () => {
      if (confirmedStatus === 'confirmed_match') return 'bg-green-100';
      if (confirmedStatus === 'confirmed_former') return 'bg-gray-100';
      
      switch (type) {
        case 'exact': return 'bg-green-50';
        case 'likely': return 'bg-blue-50';
        case 'review': return 'bg-amber-50';
        case 'missing': return 'bg-orange-50';
        case 'former': return 'bg-red-50';
        default: return 'bg-gray-50';
      }
    };

    // Show confirm buttons for "likely" and "review" types that aren't already confirmed
    const showConfirmButtons = (type === 'likely' || type === 'review') && !confirmedStatus;
    
    // Get status label for badge display
    const getStatusLabel = (status) => {
      const labels = {
        'confirmed_match': 'Bestätigt',
        'confirmed_former': 'Ehemalig',
        'confirmed_new': 'Neu anlegen',
        'needs_review': 'Zu prüfen',
        'ignored': 'Ignoriert'
      };
      return labels[status] || status;
    };
    
    // Get status badge color
    const getStatusBadgeClass = (status) => {
      const classes = {
        'confirmed_match': 'bg-green-600',
        'confirmed_former': 'bg-gray-500',
        'confirmed_new': 'bg-orange-500',
        'needs_review': 'bg-amber-500',
        'ignored': 'bg-slate-400'
      };
      return classes[status] || 'bg-gray-500';
    };

    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className={`rounded-lg border ${getBgColor()}`} style={{ borderColor: colors.border }}>
          <CollapsibleTrigger asChild>
            <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/50 transition-colors">
              {getIcon()}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate" style={{ color: colors.dark }}>
                  {person.name || person.employee?.name || 'Unbekannt'}
                </p>
                <div className="flex items-center gap-2 text-xs" style={{ color: colors.gray }}>
                  {(person.email || person.employee?.email) && (
                    <span className="truncate">{person.email || person.employee?.email}</span>
                  )}
                  {(person.department || person.employee?.department) && (
                    <span className="flex items-center gap-1">
                      <Building className="h-3 w-3" />
                      {person.department || person.employee?.department}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Confirmed Status Badge */}
              {confirmedStatus && (
                <Badge className={getStatusBadgeClass(confirmedStatus)}>
                  {getStatusLabel(confirmedStatus)}
                </Badge>
              )}
              
              {/* Match Type Badge */}
              {!confirmedStatus && matchType && (
                <Badge variant="outline" className="text-xs">
                  {matchType === 'email' ? 'E-Mail' : 
                   matchType === 'name' ? 'Name' : 
                   matchType === 'firstname_lastname' ? 'Vor-/Nachname' :
                   matchType === 'lastname_only' ? 'Nachname' : matchType}
                </Badge>
              )}
              {!confirmedStatus && reason && (
                <Badge variant="outline" className="text-xs text-amber-600">
                  {reason}
                </Badge>
              )}
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-3 pb-3 pt-1 text-xs border-t" style={{ borderColor: colors.border }}>
              {/* Confirm Buttons with Dropdown */}
              {showConfirmButtons && (
                <div className="flex gap-2 mb-3 py-2">
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onConfirm(person, 'confirmed_match', category);
                    }}
                    disabled={isThisConfirming}
                    className="flex-1"
                    style={{ background: colors.blue }}
                    data-testid={`confirm-match-${personKey}`}
                  >
                    {isThisConfirming ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <Check className="h-3 w-3 mr-1" />
                    )}
                    Bestätigen
                  </Button>
                  
                  {/* Dropdown for other options */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isThisConfirming}
                        data-testid={`status-dropdown-${personKey}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem 
                        onClick={() => onConfirm(person, 'confirmed_former', category)}
                        className="cursor-pointer"
                      >
                        <UserMinus className="h-4 w-4 mr-2 text-gray-500" />
                        Ehemalig
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => onConfirm(person, 'confirmed_new', category)}
                        className="cursor-pointer"
                      >
                        <UserPlus className="h-4 w-4 mr-2 text-orange-500" />
                        Neu anlegen
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => onConfirm(person, 'needs_review', category)}
                        className="cursor-pointer"
                      >
                        <HelpCircle className="h-4 w-4 mr-2 text-amber-500" />
                        Zu prüfen
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => onConfirm(person, 'ignored', category)}
                        className="cursor-pointer text-slate-500"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Ignorieren
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
              
              {person.possible_matches && (
                <div className="mb-2">
                  <p className="font-medium mb-1" style={{ color: colors.dark }}>Mögliche Übereinstimmungen:</p>
                  {person.possible_matches.map((match, idx) => (
                    <div key={idx} className="ml-2 mb-1">
                      • {match.name} {match.email && `(${match.email})`}
                    </div>
                  ))}
                </div>
              )}
              {person.eMatrixsys && (
                <div className="mb-2">
                  <p className="font-medium mb-1" style={{ color: colors.dark }}>eMatrixsys-Eintrag:</p>
                  <div className="ml-2">
                    {person.eMatrixsys.name} {person.eMatrixsys.email && `(${person.eMatrixsys.email})`}
                  </div>
                </div>
              )}
              <pre className="bg-white p-2 rounded overflow-x-auto" style={{ color: colors.gray }}>
                {JSON.stringify(person.data || person.employee?.data || person.raw_data, null, 2)?.substring(0, 500)}
              </pre>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.dark }}>Mitarbeiterabgleich</h1>
            <p className="mt-1" style={{ color: colors.gray }}>
              Vergleichen Sie eMatrixsys-Export mit Ihrer Mitarbeiterliste
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.blue }} />
            <span className="ml-3" style={{ color: colors.gray }}>Lade Daten...</span>
          </div>
        ) : !result ? (
          /* Upload Section */
          <div className="grid md:grid-cols-2 gap-6">
            {/* Data Status Card */}
            <Card className="bg-white" style={{ borderColor: colors.border }}>
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: colors.dark }}>Datenstand (eMatrixsys)</CardTitle>
                <CardDescription style={{ color: colors.gray }}>
                  Accounts aus dem geladenen Datenstand werden für den Vergleich verwendet
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!isDataLoaded ? (
                  <div className="text-center p-6 border-2 border-dashed rounded-lg" style={{ borderColor: colors.border }}>
                    <AlertCircle className="h-10 w-10 mx-auto mb-3 text-amber-500" />
                    <p className="font-medium" style={{ color: colors.dark }}>
                      Kein Datenstand geladen
                    </p>
                    <p className="text-sm mt-2" style={{ color: colors.gray }}>
                      Bitte laden Sie zuerst einen Datenstand über die Sidebar.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-4 p-4 rounded-lg" style={{ background: colors.bg }}>
                    <Database className="h-10 w-10" style={{ color: colors.blue }} />
                    <div className="flex-1">
                      <p className="font-medium" style={{ color: colors.dark }}>
                        {dataStatus?.system_name || 'Datenstand'}
                      </p>
                      <p className="text-sm" style={{ color: colors.gray }}>
                        {dataStatus?.accounts_count?.toLocaleString()} Accounts geladen
                      </p>
                    </div>
                    <CheckCircle className="h-6 w-6 text-green-500" />
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white" style={{ borderColor: colors.border }}>
              <CardHeader>
                <CardTitle className="text-lg" style={{ color: colors.dark }}>Mitarbeiterliste</CardTitle>
                <CardDescription style={{ color: colors.gray }}>
                  Laden Sie die aktuelle Mitarbeiterliste des Kunden hoch
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FileUploadZone
                  file={employeeFile}
                  setFile={setEmployeeFile}
                  title="Mitarbeiterliste hochladen"
                  description="Unterstützt: CSV, XLSX, XLS"
                  dragOver={dragOverEmployee}
                  setDragOver={setDragOverEmployee}
                  testId="employee-upload"
                />
              </CardContent>
            </Card>

            <div className="md:col-span-2">
              <Button
                onClick={handleCompare}
                disabled={!isDataLoaded || !employeeFile || isComparing}
                className="w-full"
                size="lg"
                data-testid="compare-button"
                style={{ background: colors.dark }}
              >
                {isComparing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Vergleich läuft...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-5 w-5" />
                    Vergleich starten
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          /* Results Section */
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="bg-white border-l-4 border-l-green-500" style={{ borderColor: colors.border }}>
                <CardContent className="pt-4">
                  <div className="text-center">
                    <CheckCircle className="h-6 w-6 mx-auto text-green-500 mb-1" />
                    <div className="text-2xl font-bold text-green-600">
                      {result.summary?.exact_matches || 0}
                    </div>
                    <div className="text-xs" style={{ color: colors.gray }}>100% Match</div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-blue-500" style={{ borderColor: colors.border }}>
                <CardContent className="pt-4">
                  <div className="text-center">
                    <User className="h-6 w-6 mx-auto text-blue-500 mb-1" />
                    <div className="text-2xl font-bold text-blue-600">
                      {result.summary?.likely_matches || 0}
                    </div>
                    <div className="text-xs" style={{ color: colors.gray }}>Wahrscheinlich</div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-amber-500" style={{ borderColor: colors.border }}>
                <CardContent className="pt-4">
                  <div className="text-center">
                    <HelpCircle className="h-6 w-6 mx-auto text-amber-500 mb-1" />
                    <div className="text-2xl font-bold text-amber-600">
                      {result.summary?.needs_review || 0}
                    </div>
                    <div className="text-xs" style={{ color: colors.gray }}>Manuell prüfen</div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-orange-500" style={{ borderColor: colors.border }}>
                <CardContent className="pt-4">
                  <div className="text-center">
                    <UserPlus className="h-6 w-6 mx-auto text-orange-500 mb-1" />
                    <div className="text-2xl font-bold text-orange-600">
                      {result.summary?.missing_in_eMatrixsys || 0}
                    </div>
                    <div className="text-xs" style={{ color: colors.gray }}>Neu anlegen</div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-red-500" style={{ borderColor: colors.border }}>
                <CardContent className="pt-4">
                  <div className="text-center">
                    <UserMinus className="h-6 w-6 mx-auto text-red-500 mb-1" />
                    <div className="text-2xl font-bold text-red-600">
                      {result.summary?.former_employees || 0}
                    </div>
                    <div className="text-xs" style={{ color: colors.gray }}>Ehemalige MA</div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detected Columns Info */}
            {result.detected_columns && (
              <Card className="bg-white" style={{ borderColor: colors.border }}>
                <CardContent className="py-3">
                  <div className="flex flex-wrap gap-4 text-xs" style={{ color: colors.gray }}>
                    <span><strong>eMatrixsys erkannt:</strong> {Object.entries(result.detected_columns.eMatrixsys || {}).map(([k,v]) => `${k}="${v}"`).join(', ')}</span>
                    <span><strong>Mitarbeiterliste erkannt:</strong> {Object.entries(result.detected_columns.employee || {}).map(([k,v]) => `${k}="${v}"`).join(', ')}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Detail Tabs */}
            <Card className="bg-white" style={{ borderColor: colors.border }}>
              <Tabs defaultValue="exact" className="w-full">
                <CardHeader>
                  <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="exact" className="text-xs" data-testid="tab-exact">
                      100% ({result.summary?.exact_matches || 0})
                    </TabsTrigger>
                    <TabsTrigger value="likely" className="text-xs" data-testid="tab-likely">
                      Wahrscheinl. ({result.summary?.likely_matches || 0})
                    </TabsTrigger>
                    <TabsTrigger value="review" className="text-xs" data-testid="tab-review">
                      Prüfen ({result.summary?.needs_review || 0})
                    </TabsTrigger>
                    <TabsTrigger value="missing" className="text-xs" data-testid="tab-missing">
                      Neu ({result.summary?.missing_in_eMatrixsys || 0})
                    </TabsTrigger>
                    <TabsTrigger value="former" className="text-xs" data-testid="tab-former">
                      Ehemalig ({result.summary?.former_employees || 0})
                    </TabsTrigger>
                  </TabsList>
                </CardHeader>
                <CardContent>
                  <TabsContent value="exact" className="mt-0">
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2">
                        {result.exact_matches?.length > 0 ? (
                          result.exact_matches.map((item, idx) => (
                            <PersonCard 
                              key={idx} 
                              person={item} 
                              type="exact" 
                              matchType={item.match_type}
                              onConfirm={handleConfirmMatch}
                              isConfirming={isConfirming}
                              confirmedStatus={confirmedItems[`${item.employee?.email || item.email || ''}_${item.employee?.name || item.name || ''}`]}
                              category="exact"
                            />
                          ))
                        ) : (
                          <p className="text-center py-8" style={{ color: colors.gray }}>
                            Keine 100% Übereinstimmungen gefunden
                          </p>
                        )}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                  
                  <TabsContent value="likely" className="mt-0">
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2">
                        {result.likely_matches?.length > 0 ? (
                          result.likely_matches.map((item, idx) => (
                            <PersonCard 
                              key={idx} 
                              person={item} 
                              type="likely" 
                              matchType={item.match_type}
                              onConfirm={handleConfirmMatch}
                              isConfirming={isConfirming}
                              confirmedStatus={confirmedItems[`${item.employee?.email || item.email || ''}_${item.employee?.name || item.name || ''}`]}
                              category="likely"
                            />
                          ))
                        ) : (
                          <p className="text-center py-8" style={{ color: colors.gray }}>
                            Keine wahrscheinlichen Übereinstimmungen gefunden
                          </p>
                        )}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                  
                  <TabsContent value="review" className="mt-0">
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2">
                        {result.needs_review?.length > 0 ? (
                          result.needs_review.map((item, idx) => (
                            <PersonCard 
                              key={idx} 
                              person={item} 
                              type="review" 
                              reason={item.reason}
                              onConfirm={handleConfirmMatch}
                              isConfirming={isConfirming}
                              confirmedStatus={confirmedItems[`${item.employee?.email || item.email || ''}_${item.employee?.name || item.name || ''}`]}
                              category="review"
                            />
                          ))
                        ) : (
                          <p className="text-center py-8" style={{ color: colors.gray }}>
                            Keine Einträge zur manuellen Prüfung
                          </p>
                        )}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                  
                  <TabsContent value="missing" className="mt-0">
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2">
                        {result.missing_in_eMatrixsys?.length > 0 ? (
                          result.missing_in_eMatrixsys.map((item, idx) => (
                            <PersonCard 
                              key={idx} 
                              person={item} 
                              type="missing"
                              onConfirm={handleConfirmMatch}
                              isConfirming={isConfirming}
                              confirmedStatus={confirmedItems[`${item.email || ''}_${item.name || ''}`]}
                              category="missing"
                            />
                          ))
                        ) : (
                          <p className="text-center py-8" style={{ color: colors.gray }}>
                            Alle Mitarbeiter sind bereits im eMatrixsys
                          </p>
                        )}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                  
                  <TabsContent value="former" className="mt-0">
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2">
                        {result.former_employees?.length > 0 ? (
                          result.former_employees.map((item, idx) => (
                            <PersonCard 
                              key={idx} 
                              person={item} 
                              type="former"
                              onConfirm={handleConfirmMatch}
                              isConfirming={isConfirming}
                              category="former"
                              confirmedStatus={confirmedItems[`${item.email || ''}_${item.name || ''}`]}
                            />
                          ))
                        ) : (
                          <p className="text-center py-8" style={{ color: colors.gray }}>
                            Keine ehemaligen Mitarbeiter gefunden
                          </p>
                        )}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={handleDownload} className="flex-1" data-testid="download-button" style={{ background: colors.blue }}>
                <Download className="mr-2 h-4 w-4" />
                Report (CSV)
              </Button>
              <Button variant="outline" onClick={resetAll} className="flex-1" data-testid="new-comparison-button">
                <RefreshCw className="mr-2 h-4 w-4" />
                Neuer Vergleich
              </Button>
              <Button
                variant="destructive"
                onClick={() => setIsDeleteOpen(true)}
                className="flex-1"
                data-testid="delete-data-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Daten löschen
              </Button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Daten löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie alle Daten dieses Vergleichs wirklich löschen? 
                Dies umfasst die hochgeladenen Dateien und den erstellten Report. 
                Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isDeleting}
                data-testid="confirm-delete-data"
              >
                {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Endgültig löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default MitarbeiterAbgleich;
